import type { FC } from 'react';
import { useCallback, useMemo } from 'react';
import { useResizeableContainer } from '@ContractBuilder/hooks';
import { motion } from 'motion/react';

import { DOCUMENT_PADDING_PX, getAnimationProps, MAX_WIDTH_OFFSET_PX } from '../constants';
import { useInitialWidth } from '../hooks/useInitialWidth';
import { selectActiveTab, selectIsInSplitMode, selectSplitTab, usePDFPreviewStore } from '../store/pdf-preview.store';
import type { GetTabProps, RenderTabsFn, TabSlotProps } from '../types';
import { getPlacementFlags } from '../utils/getPlacementFlags';

import { pdfPreviewPanelClasses } from './classes';
import { PDFPreviewHeader } from './PDFPreviewHeader';
import { ResizeHandle } from './ResizeHandle';
import { SplitPaneContainer } from './SplitPaneContainer';

interface PDFPreviewShellProps {
  isLoading: boolean;
  onClose: () => void;
  renderTabs: RenderTabsFn;
}

export const PDFPreviewShell: FC<PDFPreviewShellProps> = ({ renderTabs, isLoading, onClose }) => {
  const { placement, activeTabId, splitTabId, isInSplitMode, splitTab } = usePDFPreviewStore((state) => ({
    placement: state.placement,
    activeTabId: state.activeTabId,
    splitTabId: state.splitTabId,
    isInSplitMode: selectIsInSplitMode(state),
    activeTab: selectActiveTab(state),
    splitTab: selectSplitTab(state),
  }));

  const { initialWidth, onResize, windowWidth } = useInitialWidth();
  const animationProps = useMemo(() => getAnimationProps(placement), [placement]);

  const { onMouseDown, width, pageWidth } = useResizeableContainer({
    debounceMs: 100,
    initialWidth,
    maxWidthOffset: isInSplitMode ? MAX_WIDTH_OFFSET_PX : 0,
    onResized: onResize,
    placement,
  });

  const { isLeftSidePlacement, isRightSidePlacement } = getPlacementFlags(placement, isInSplitMode);
  const splitPageWidth = windowWidth - pageWidth - DOCUMENT_PADDING_PX * 2;

  const getMainPaneTabProps: GetTabProps = useCallback(
    (tabId: string): TabSlotProps => ({
      id: tabId,
      isActive: tabId === activeTabId,
      isSplitPane: false,
      pageWidth,
      placement,
    }),
    [pageWidth, placement, activeTabId],
  );

  const getSplitPaneTabProps: GetTabProps = useCallback(
    (tabId: string): TabSlotProps => ({
      id: tabId,
      isActive: tabId === splitTabId,
      isSplitPane: true,
      pageWidth: splitPageWidth,
      placement,
    }),
    [splitPageWidth, placement, splitTabId],
  );

  const mainPaneContent = renderTabs({ getTabProps: getMainPaneTabProps });
  const splitPaneContent = renderTabs({ getTabProps: getSplitPaneTabProps });

  return (
    <div className="flex">
      {isInSplitMode && splitTab && (
        <SplitPaneContainer fileName={splitTab.fileName ?? splitTab.label} width={windowWidth - width}>
          {splitPaneContent}
        </SplitPaneContainer>
      )}

      <motion.div
        {...animationProps}
        className={pdfPreviewPanelClasses({
          isLeftSidePlacement,
          isRightSidePlacement,
        })}
        style={{ width }}
      >
        <div className="relative flex h-full w-full shrink-0 grow-0 flex-col bg-gray-100 px-3 py-1.5">
          <PDFPreviewHeader isLoading={isLoading} onClose={onClose} />
          {mainPaneContent}
          <ResizeHandle
            isLeftSidePlacement={isLeftSidePlacement}
            isRightSidePlacement={isRightSidePlacement}
            onMouseDown={onMouseDown}
          />
        </div>
      </motion.div>
    </div>
  );
};
